import { useTranslator, RichTranslator } from '@bluebeam/react-i18n';
import Location from 'components/Icons/Location';
import React, { useEffect, useState } from 'react';
import {
  GoogleMap,
  GoogleMapProps,
  Marker,
  withGoogleMap,
} from 'react-google-maps';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { Project } from 'records/Project';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { geocode } from 'services/mapService';

interface Props extends WithAppStoreProps {
  onEditClicked: () => void;
  selectedProjectData: Project | null;
  zoom?: number;
  mapOptions?: any;
}

const MapWithAMarker = withGoogleMap((props: GoogleMapProps) => (
  <GoogleMap
    defaultZoom={props.defaultZoom}
    defaultCenter={props.defaultCenter}
    options={props.options}
    center={props.center}
  >
    <Marker position={props.center} />
  </GoogleMap>
));

const defaultCenter = { lat: 34.14, lng: -118.16 };

const LocationCard: React.FC<Props> = (props: Props) => {
  const { selectedProjectData, zoom, mapOptions, onEditClicked } = props;
  const { translate } = useTranslator(['roverDashboard']);

  const [lng, setLng] = useState<number>(0);
  const [lat, setLat] = useState<number>(0);
  const [showMap, setShowMap] = useState<boolean>(false);

  const initMap = async () => {
    if (!selectedProjectData) {
      return;
    }

    const address = {
      street: selectedProjectData.street,
      city: selectedProjectData.city,
      state: selectedProjectData.state,
      zipcode: selectedProjectData.zipcode,
    };
    const coordinates = await geocode(address);

    if (coordinates) {
      setLat(coordinates.center.latitude);
      setLng(coordinates.center.longitude);
      setShowMap(true);
    }
  };

  useEffect(() => {
    if (!selectedProjectData) {
      return;
    }

    initMap();
  }, []);

  return (
    <div id="project-dashboard-location" className="w100">
      <div className="medium font-size-5 color-gray-7">
        <div className="flex items-center mb2">
          <div className="wrem-1p5 hrem-1p5 mr1 icon flex-center primary-blue">
            <Location width="24px" height="24px" />
          </div>
          <div>{translate('roverDashboard:location')}</div>
        </div>
      </div>
      <div className="b-radius-3 bgd-section h100 hrem-6">
        <div className="h100 w100">
          {selectedProjectData && selectedProjectData.street.length > 0 ? (
            <div className="flex row h100">
              <div className="flex-center grow-1 w100 m3">
                <div className="w100">
                  <div className="color-gray-7">
                    {selectedProjectData.street}
                  </div>
                  <div className="color-gray-7">
                    {selectedProjectData.city}, {selectedProjectData.state}
                    {selectedProjectData.zipcode}
                  </div>
                </div>
                <div
                  className="flex-center grow-1 h100 w100"
                  style={{
                    height: '60px',
                    width: '115px',
                  }}
                >
                  {showMap && (
                    <MapWithAMarker
                      containerElement={
                        <div style={{ height: `60px`, width: '115px' }} />
                      }
                      mapElement={
                        <div style={{ height: `100%`, width: '100%' }} />
                      }
                      options={mapOptions}
                      defaultCenter={defaultCenter}
                      center={{ lat, lng }}
                      defaultZoom={zoom}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-center grow-1 h100 w100">
              <div className="font-normal text-[16px]">
                <RichTranslator
                  components={[
                    // eslint-disable-next-line jsx-a11y/control-has-associated-label
                    <button
                      type="button"
                      css={{ fontFamily: 'Roboto, sans-serif' }}
                      className="cursor-pointer border-none bg-transparent text-[16px] p-0 font-medium text-primary-blue hover:underline"
                      onClick={(): void => {
                        onEditClicked();
                      }}
                    />,
                  ]}
                  translationKey="roverDashboard:emptyLocation"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

LocationCard.defaultProps = {
  zoom: 15,
  mapOptions: { fullscreenControl: false },
};

export default withAppStore<Props>(LocationCard);
